import { createContext, useContext } from "react"

const STORAGE_KEY = "debug:uiDensity"

export type UiDensity = "compact" | "default" | "comfortable" | "spacious"

export const UI_DENSITY_OPTIONS: { value: UiDensity; label: string }[] = [
  { value: "compact", label: "Compact" },
  { value: "default", label: "Default" },
  { value: "comfortable", label: "Comfortable" },
  { value: "spacious", label: "Spacious" },
]

/** Concrete spacing/sizing tokens for each density level */
export type DensityTokens = {
  sidebarWidth: number
  listItemPy: number
  sectionPx: number
  sectionPy: number
  chipHeight: number
  chipFontSize: number
  fontSize: string
  buttonPy: number
}

const densityMap: Record<UiDensity, DensityTokens> = {
  compact: {
    sidebarWidth: 240,
    listItemPy: 0.4,
    sectionPx: 1.5,
    sectionPy: 0.5,
    chipHeight: 20,
    chipFontSize: 11,
    fontSize: "0.775rem",
    buttonPy: 0.6,
  },
  default: {
    sidebarWidth: 280,
    listItemPy: 0.75,
    sectionPx: 2,
    sectionPy: 1,
    chipHeight: 22,
    chipFontSize: 12,
    fontSize: "0.8125rem",
    buttonPy: 1,
  },
  comfortable: {
    sidebarWidth: 300,
    listItemPy: 1,
    sectionPx: 2.5,
    sectionPy: 1.25,
    chipHeight: 24,
    chipFontSize: 12,
    fontSize: "0.875rem",
    buttonPy: 1.2,
  },
  spacious: {
    sidebarWidth: 320,
    listItemPy: 1.25,
    sectionPx: 3,
    sectionPy: 1.5,
    chipHeight: 26,
    chipFontSize: 13,
    fontSize: "0.9375rem",
    buttonPy: 1.4,
  },
}

export const getDensityTokens = (density: UiDensity): DensityTokens =>
  densityMap[density]

export type UiDensityContextValue = {
  density: UiDensity
  setDensity: (d: UiDensity) => void
  tokens: DensityTokens
}

export const UiDensityContext = createContext<UiDensityContextValue>({
  density: "default",
  setDensity: () => {},
  tokens: densityMap.default,
})

export const useUiDensity = () => useContext(UiDensityContext)

export const getInitialDensity = (): UiDensity => {
  const stored = localStorage.getItem(STORAGE_KEY) as UiDensity | null
  if (stored && stored in densityMap) return stored
  return "default"
}

export const persistDensity = (d: UiDensity) => {
  localStorage.setItem(STORAGE_KEY, d)
}
